<template>
	<div v-if="!loading" class="p-20">
		<!-- SUBHEADER -->
		<div class="disp-flex">
			<!-- TITLE -->
			<div class="mb-20">
				<h1 class="mt-0">
					<template v-if="currentTemplate">
						<div v-if="currentTemplate.template_id">
							<svg-icon icon-class="file-code-solid" />
							{{
								$t('templateDetailTitle', {
									value: currentTemplate.template_id
								})
							}}
							{{ translate_template_id(currentTemplate.template_id) }}
							&mdash;
							{{ $translate.channel(currentTemplate.channel) }}
							&mdash;
							{{ $translate.language(currentTemplate.locale) }}
						</div>
						<div v-else>
							<svg-icon icon-class="file-code-solid" />
							{{ $t('templateDetailTitle', { value: '[No document name.]' }) }}
						</div>
					</template>
				</h1>
			</div>
			<!-- END TITLE -->
			<!-- OPERATOR ACTIONS -->
			<!-- END OPERATOR ACTIONS -->
		</div>
		<!-- END SUBHEADER -->
		<!-- COMPONENT CONTENT -->
		<el-row :gutter="15">
			<el-col :span="24">
				<template
					v-if="
						currentTemplate &&
							(currentTemplate.channel === 'EMAIL' || currentTemplate.channel === 'MAIL' || currentTemplate.channel === 'SMS' || currentTemplate.channel === 'NONE')
					"
				>
					<div>
						<template v-if="currentTemplate.type === 'WORD' || currentTemplate.type === 'WORD2'">
							<el-row>
								<el-col :span="12">
									<el-button type="primary" class="mb-20" :loading="downloadLoading" @click="handleDownload" >
										{{ $t('downloadWordRendered') }}
									</el-button>
									<el-button type="primary" @click="handleDownloadWordTemplate">
										{{ $t('downloadWordTemplate') }}
									</el-button>
								</el-col>
								<el-col :span="12">
									<div>
										<input
											id="file"
											ref="file"
											accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document"
											type="file"
											name="file"
											class="inputfile"
											@change="handleFileChange"
										/>
										<label for="file" class="cursor-pointer">{{ $t('chooseFileToUpload') }}</label>
										<div v-if="selectedFile" class="mb-15">{{ $t('selectedFile') }}: {{ selectedFile }}</div>
									</div>
									<el-button v-if="selectedFile" type="primary" @click="handleUpload">
										{{ $t('uploadWord') }}
									</el-button>
								</el-col>
							</el-row>
						</template>
						<template v-if="currentTemplate.type === 'LIQUID' || currentTemplate.type === 'PYTHON_TEMPLATE'">
							<el-row>
								<el-col>
									<el-button type="primary" size="mini" :loading="downloadLoading" @click="handleDownload">
										{{ $t('downloadExample') }}
									</el-button>
									<el-button v-if="currentTemplate.template_renderer_version === 'v3'" type="success" size="mini" @click="handleSaveTemplate">
										{{ $t('save') }}
									</el-button>
									<span v-if="dev" class="dev">
										<el-button size="mini" @click="handleToggleRawEditor">Toggle raw editor</el-button>
										<el-select :value="currentTemplate.template_renderer_version" :loading="loadingUpdateTemplateRendererVersion" size="mini" @change="handleTemplateRendererVersionChange">
											<el-option value="v2">V2</el-option>
											<el-option value="v3">V3</el-option>
										</el-select>
										<span class="dev__label">DEV</span>
									</span>
								</el-col>
							</el-row>
							<el-row>
								<div v-if="currentTemplate.channel === 'EMAIL'">
									<el-form ref="form" :model="subjectForm" label-width="120px" label-position="top">
										<el-form-item :label="$t('subjectTemplate')">
											<el-input v-model="subjectForm.subject"></el-input>
										</el-form-item>
									</el-form>
								</div>
								<div class="gjs__container">
									<div class="gjs__content">
										<div v-if="showRawEditor || currentTemplate.channel === 'SMS'">
											<textarea v-if="currentTemplate.channel === 'SMS'" v-model="tinymcecontent" style="height: 240px !important; width: 480px;" class="gjs"></textarea>
											<textarea v-else v-model="tinymcecontent" style="width: 100%" class="gjs"></textarea>
										</div>
										<div v-else>
											<div id="gjs" class="gjs"></div>
										</div>
									</div>
									<div v-if="currentTemplate.template_renderer_version !== 'v3'" class="gjs__overlay">{{ $t('template.detail.contact_support') }}</div>
								</div>
							</el-row>
						</template>
					</div>
				</template>
			</el-col>
		</el-row>
	</div>
	<div v-else>
		<loader class="mt-50 mb-50 ml-50"> </loader>
	</div>
</template>
<script>
import { GET_TEMPLATE, UPDATE_COMMUNICATION_TEMPLATE } from '@/gql';
import axios from 'axios';
import Loader from '@/components/_Loaders';
import { Base64 } from 'js-base64';
import { mapGetters } from 'vuex';
import Logging from '@/logging';
import grapesjs from 'grapesjs';
const grapesjsPresetNewsletter = require('grapesjs-preset-newsletter').default;
import gql from 'graphql-tag';
import i18n from '@/i18n';
import TranslationHelper from '@/helpers/translation';
import { renderTemplate } from '@/api/templating';

const GET_ACTION_ID = gql`
	query viewsTemplates_actionId($participant: uuid!, $template_id: String!) {
		actionId: risolto_view_legal_entity_template(
			where: {
			participant: { _eq: $participant }
			_or: {
				template_id: { _eq: $template_id }
				default_template_id: { _eq: $template_id }
			}
			}
		) {
			id
		}
	}
`;

const GET_INVOICE_DOCUMENT = gql`
	query viewsTemplates_invoiceDocument($participant: uuid!) {
		risolto_invoice_document(
			where: {
				participant: { _eq: $participant }
			}
			order_by: {
				created_at: desc
			}
		) {
			reference
			recipient
		}
	}
`;

const GET_PREVIEW_ACTION = gql`
	query viewsTemplates_previewAction($participant: uuid!, $message_code: String!) {
		previewAction: risolto_action(where: {
			_and: {
			participant: { _eq: $participant }
				message_code: { _eq: $message_code }
			}
		}) {
			reference
			legal_entity
		}
	}
`;

const UPDATE_TEMPLATE_RENDERER_VERSION = gql`
	mutation ($participant: uuid!, $communication_template: uuid!, $template_renderer_version: String!){
		update_risolto_communication_template(
			_set: {
				template_renderer_version: $template_renderer_version
			},
			where: {
				participant: { _eq: $participant },
				reference: { _eq: $communication_template }
			}
		) {
			affected_rows
		}
	}
`;

export default {
	components: {
		Loader
	},
	data() {
		return {
			subjectForm: {
				subject: ''
			},
			subjectFormRules: {
				subject: [{ required: true, trigger: 'blur' }]
			},
			tinymcecontent: '',
			editor: null,
			renderedWordBase64: undefined,
			currentTemplate: {},
			isRendered: false,
			renderedHTMLBase64: undefined,
			renderedHTMLUri: undefined,
			renderedHTML: undefined,
			selectedFile: undefined,
			loading: false,
			downloadLoading: false,
			isDirty: false,
			showRawEditor: false,
			loadingUpdateTemplateRendererVersion: false
		};
	},
	computed: {
		...mapGetters(['dev', 'selectedParticipantStore'])
	},
	updated() {
		if (this.editor === null) {
			this.initGrapesJs();
		}
	},
	created() {
		this.loading = true;
		this.$apollo
			.query({
				query: GET_TEMPLATE,
				variables: {
					reference: this.$route.params.id
				}
			})
			.then(({ data }) => {
				this.currentTemplate = data.template[0] || '';
				if (this.currentTemplate === '') {
					alert('No template.');
				}

				this.tinymcecontent = Base64.decode(this.currentTemplate.document_template.template_string);
				this.subjectForm.subject = this.currentTemplate.subject_template;
				this.loading = false;
			})
			.catch(err => {
				Logging.log('Error: ', err);
			});
	},
	methods: {
		initGrapesJs() {
			const myNewComponentTypes = editor => {
				editor.DomComponents.addType('account-overview', {
					isComponent: el => el.tagName === 'ACCOUNT-OVERVIEW',

					model: {
						defaults: {
							tagName: 'account-overview',
							components: `<div>{{ includes.account_overview }}</div>`
						}
					},
					view: {
						onRender({ el }) {
							el.innerHTML = `<div>{{ account_overview }}</div>`;
						}
					}
				});
				editor.DomComponents.addType('payment-link', {
					isComponent: el => el.tagName === 'PAYMENT-LINK',

					model: {
						defaults: {
							tagName: 'payment-link',
							components: `<div>{{ includes.payment_link }}</div>`
						}
					},
					view: {
						onRender({ el }) {
							el.innerHTML = `<div>{{ payment_link }}</div>`;
						}
					}
				});
			};

			this.editor = grapesjs.init({
				clearOnRender: true,
				container: '#gjs',
				plugins: [grapesjsPresetNewsletter, myNewComponentTypes],
				fromElement: false,
				components: '',
				pluginsOpts: {
					[grapesjsPresetNewsletter]: {
						modalLabelImport: 'Paste all your code here below and click import',
						modalLabelExport: 'Copy the code and use it wherever you want',
						codeViewerTheme: 'material',
						importPlaceholder: '<table class="table"><tr><td class="cell">Hello world!</td></tr></table>',
						cellStyle: {
							'font-size': '12px',
							'font-weight': 300,
							'vertical-align': 'top',
							color: 'rgb(111, 119, 125)',
							margin: 0,
							padding: 0
						}
					}
				}
			});
			this.editor.setComponents(this.tinymcecontent);

			this.editor.BlockManager.add('payment-link', {
				label: 'Payment link',
				content: `<payment-link></payment-link>`
			});

			this.editor.BlockManager.add('account-overview', {
				label: 'Account overview',
				content: `<account-overview></account-overview>`
			});
		},
		handleSaveTemplate() {
			let html;
			if (this.showRawEditor || this.currentTemplate.channel === 'SMS') {
				html = this.tinymcecontent;
			} else {
				if (this.editor.getModel().isEditing()) this.editor.getSelected().view.disableEditing();
				html = this.editor.Commands.get('gjs-get-inlined-html').run(this.editor);
			}
			const fileBase64 = Base64.encode(html);
			const newDocTemplate = { _type: 'inline', template_string: fileBase64 };

			const variables = {
				document_template: newDocTemplate,
				subject_template: this.subjectForm.subject,
				template_reference: this.$route.params.id
			};

			this.$apollo
				.mutate({
					mutation: UPDATE_COMMUNICATION_TEMPLATE,
					variables: variables
				})
				.then(result => {
					this.$notify({
						title: this.$t('success'),
						message: this.$t('templateUpdated'),
						type: 'success'
					});
				})
				.catch(e => {
					Logging.error(e);
					alert('Error');
				});
		},
		handleFileChange() {
			Logging.log('document.getElementById: ', document.getElementById('file').files[0]);
			this.selectedFile = document.getElementById('file').files[0].name;
		},
		handleDownloadWordTemplate() {
			var downloadLink = document.createElement('a');
			downloadLink.id = 'downloadLinkTemplate';
			downloadLink.href = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64, ${this.currentTemplate.document_template.template_string}`;
			document.getElementById('app').appendChild(downloadLink);
			Logging.log('downloadLink: ', downloadLink);
			document.getElementById('downloadLinkTemplate').click();
		},
		async handleDownload() {
			if (this.downloadLoading) {
				return;
			}
			this.downloadLoading = true;
			let template_query_variables = {};
			let previewActionLegalEntity = this.currentTemplate.legal_entity;

			if (this.currentTemplate.template_id === 'invoice') {
				let result = await this.$apollo.query({
					query: GET_INVOICE_DOCUMENT,
					variables: {
						participant: this.selectedParticipantStore
					}
				});
				let invoiceDocument = result.data.risolto_invoice_document.length > 0 ? result.data.risolto_invoice_document[0] : null;
				template_query_variables = {
					invoice: invoiceDocument.reference,
					recipient: invoiceDocument.recipient
				};
			} else {
				let previewActionReference;
				let result = await this.$apollo.query({
					query: GET_ACTION_ID,
					variables: {
						participant: this.selectedParticipantStore,
						template_id: this.currentTemplate.template_id
					}
				});
				let actionId = result.data.actionId.length > 0 ? result.data.actionId[0].id : null;
				if (actionId) {
					let result = await this.$apollo.query({
						query: GET_PREVIEW_ACTION,
						variables: {
							participant: this.selectedParticipantStore,
							message_code: actionId
						}
					});
					previewActionReference = result.data.previewAction.length > 0 ? result.data.previewAction[0].reference : null;
					previewActionLegalEntity = result.data.previewAction.length > 0 ? result.data.previewAction[0].legal_entity : null;
					template_query_variables = {
						action: previewActionReference
					};
				}
			}

			var downloadLink = document.createElement('a');
			const downloadLinkId = `downloadLink${Math.round(Math.random() * 100000000)}`;
			downloadLink.id = downloadLinkId;

			if (this.currentTemplate.type === 'WORD' || this.currentTemplate.type === 'WORD2') {
				downloadLink.download = `${this.currentTemplate.template_id}.docx`;
				Logging.log(downloadLink.download);
			} else if (this.currentTemplate.type === 'PYTHON_TEMPLATE' || this.currentTemplate.type === 'LIQUID') {
				downloadLink.download = `${this.currentTemplate.template_id}.html`;
				Logging.log(downloadLink.download);
			} else {
				alert('type not found');
			}

			let payload;

			payload = {
				participant: this.currentTemplate.participant,
				legal_entity: previewActionLegalEntity,
				channel: this.currentTemplate.channel,
				tone_of_voice: this.currentTemplate.tone_of_voice,
				locale: this.currentTemplate.locale,
				template_id: this.currentTemplate.template_id,
				template_query_variables: template_query_variables,
				sample_run: false,
				template_uri: ''
			};

			Logging.log('payload: ', payload);
			await renderTemplate(payload).then(result => {
				Logging.log('result van template renderer: ', result);
				if (this.currentTemplate.type === 'PYTHON_TEMPLATE' || this.currentTemplate.type === 'LIQUID') {
					this.renderedHTMLBase64 = result.data.base64;
					try {
						this.renderedHTMLUri = result.data.temporary_uri.uri;
					} catch (e1) {
						Logging.error(e1);
					}
					this.renderedHTML = Base64.decode(this.renderedHTMLBase64);
					this.isRendered = true;
					this.linkString = `data:text/html;base64, ${result.data.base64}`;
				} else if (this.currentTemplate.type === 'WORD' || this.currentTemplate.type === 'WORD2') {
					this.renderedWordBase64 = result.data.base64;
					this.linkString = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64, ${this.renderedWordBase64}`;
				} else {
					Logging.log('templatetype not found');
					this.loading = false;
				}

				downloadLink.href = this.linkString;

				document.getElementById('app').appendChild(downloadLink);

				document.getElementById(downloadLinkId).click();
			});

			this.downloadLoading = false;
		},
		handleUpload() {
			const _this = this;
			if (!document.getElementById('file').files[0]) {
				this.$notify({
					title: this.$t('somethingWentWrong'),
					message: this.$t('selectAFile'),
					type: 'error'
				});
				return;
			} else {
				var oFReader = new FileReader();
				oFReader.onload = function(e) {
					const splitResult = e.target.result.split(',');
					const fileBase64 = splitResult[1];

					const newDocTemplate = { _type: 'inline', template_string: fileBase64 };

					const variables = {
						document_template: newDocTemplate,
						template_reference: _this.$route.params.id
					};

					_this.$apollo
						.mutate({
							mutation: UPDATE_COMMUNICATION_TEMPLATE,
							variables: variables
						})
						.then(result => {
							_this.$notify({
								title: _this.$t('success'),
								message: _this.$t('templateUpdated'),
								type: 'success'
							});
						})
						.catch(e => {
							Logging.error(e);
							alert('Error');
						});
				};
				oFReader.readAsDataURL(document.getElementById('file').files[0]);
			}
		},
		handleToggleRawEditor() {
			this.showRawEditor = !this.showRawEditor;
			if (!this.showRawEditor) {
				this.$nextTick(() => {
					this.initGrapesJs();
				});
			}
		},
		async handleTemplateRendererVersionChange(value) {
			if (this.loadingUpdateTemplateRendererVersion) {
				return;
			}
			this.loadingUpdateTemplateRendererVersion = true;

			await this.$apollo.mutate({
				mutation: UPDATE_TEMPLATE_RENDERER_VERSION,
				variables: {
					participant: this.selectedParticipantStore,
					communication_template: this.currentTemplate.reference,
					template_renderer_version: value
				}
			});
			this.currentTemplate.template_renderer_version = value;

			this.loadingUpdateTemplateRendererVersion = false;
		},
		translate_template_id(id) {
			const translation = TranslationHelper.template_id(i18n, id);

			if (translation !== id) {
				return ` (${translation})`;
			} else {
				return '';
			}
		}
	}
};
</script>
<style lang="scss">
.emailRenderer {
	display: flex;
	justify-content: center;
}

.gjs {
	margin-top: 20px;
	height: calc(100vh - 260px) !important;
}

.gjs__container {
	display: grid;
}

.gjs__overlay {
	z-index : 5;
	background-color:rgba(0, 0, 0, 0.4);
	color: white;
	font-weight: strong;
	font-size: 26px;
	padding-top: 50px;
	text-align: center;
}

.gjs__content, .gjs__overlay {
	grid-area: 1 / 1;
}

.dev {
	background: red;
	padding: 10px;
}

.dev__label {
	color: white;
	text-transform: uppercase;
}

</style>
